import { useEffect, useRef } from 'react';

const services = [
  {
    icon: 'ri-robot-2-line',
    title: 'AI Chatbots',
    desc: 'Intelligent chatbots that handle enquiries, qualify leads, and guide customers to booking around the clock, without lifting a finger.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Automated Booking Systems',
    desc: 'Let customers book appointments directly through your website, WhatsApp, or social, with automated reminders and follow-ups.',
  },
  {
    icon: 'ri-database-2-line',
    title: 'CRM Integration',
    desc: 'Seamlessly connect your customer data across every touchpoint. Track, manage, and nurture every lead with zero manual work.',
  },
  {
    icon: 'ri-user-follow-line',
    title: 'Lead Capture & Follow-Up',
    desc: 'Never lose a lead again. Automated sequences follow up instantly via email and WhatsApp to convert interest into bookings.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Multi-Channel Automation',
    desc: 'Instagram DMs, Facebook Messenger, WhatsApp, SMS, email, website chat. Wherever your customers message you, we automate the reply.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: '24/7 Digital Assistant',
    desc: 'Your AI-powered team member that never sleeps. Answers questions, handles objections, and books clients all hours of the day.',
  },
  {
    icon: 'ri-layout-masonry-line',
    title: 'Website Design & Build',
    desc: 'Professional, fast-loading websites built to convert. Clean design, mobile-ready, and tailored to your brand from the ground up.',
  },
];

const channels = [
  { icon: 'ri-whatsapp-line', label: 'WhatsApp', color: 'text-green-400' },
  { icon: 'ri-instagram-line', label: 'Instagram', color: 'text-pink-400' },
  { icon: 'ri-facebook-circle-line', label: 'Facebook', color: 'text-sky-400' },
  { icon: 'ri-mail-line', label: 'Email', color: 'text-ea-blue' },
  { icon: 'ri-message-2-line', label: 'SMS', color: 'text-yellow-400' },
  { icon: 'ri-linkedin-line', label: 'LinkedIn', color: 'text-sky-300' },
  { icon: 'ri-tiktok-line', label: 'TikTok', color: 'text-white' },
  { icon: 'ri-global-line', label: 'Website Chat', color: 'text-purple-400' },
];

const WhatWeDoSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-ea-navy py-24" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
            <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Our Solutions</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5">
            Automate Everything That<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
              Slows Your Business Down
            </span>
          </h2>
          <p className="text-ea-gray text-lg max-w-2xl mx-auto">
            From first contact to final booking, we build AI systems that work while you sleep.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-16">
          {services.map((service) => (
            <div
              key={service.title}
              className="fade-card group bg-ea-dark-card border border-ea-dark-border hover:border-ea-blue/50 rounded-xl p-4 sm:p-6 transition-all duration-400 cursor-default"
              style={{
                opacity: 0,
                transform: 'translateY(20px)',
                transition: 'opacity 0.5s ease, transform 0.5s ease, border-color 0.3s ease',
              }}
            >
              <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center rounded-xl bg-ea-blue/10 border border-ea-blue/20 group-hover:bg-ea-blue/20 transition-colors duration-300 mb-3 sm:mb-5">
                <i className={`${service.icon} text-ea-blue text-base sm:text-xl`}></i>
              </div>
              <h3 className="text-white font-semibold text-sm sm:text-lg mb-1.5 sm:mb-2">{service.title}</h3>
              <p className="text-ea-gray text-xs sm:text-sm leading-relaxed">{service.desc}</p>
            </div>
          ))}
        </div>

        {/* Channels strip */}
        <div className="border border-ea-dark-border rounded-2xl p-8 bg-ea-dark-card/50">
          <div className="text-center mb-8">
            <p className="text-white font-semibold text-lg mb-2">Works Across Every Channel Your Customers Use</p>
            <p className="text-ea-gray text-sm max-w-xl mx-auto">
              Not just WhatsApp and email. We automate every platform your customers message you on. If they can send a message, we can automate the response.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {channels.map((ch) => (
              <div
                key={ch.label}
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-ea-dark border border-ea-dark-border hover:border-ea-blue/30 transition-colors duration-200 cursor-default"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${ch.icon} ${ch.color} text-sm`}></i>
                </div>
                <span className="text-white/80 text-sm font-medium whitespace-nowrap">{ch.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
